import React, { useState, useMemo, useEffect } from 'react';
import { Player, Position, TeamInLeague, GameState, TacticalPosition, DisplayFixture } from '../../domain/entities';
import { getPlayerValue, calculateStarRating } from '../../domain/constants';
import { PlayerComparisonModal } from '../shared/PlayerComparisonModal';
import { PlayerFace } from '../shared/PlayerFace';
import { soundService } from '../../services/SoundService';
import { View } from '../../App';
import { SquadDepthChart } from '../shared/SquadDepthChart';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { TeamFixtures } from '../shared/TeamFixtures';
import { TeamBadge } from '../shared/TeamBadge';
import { PotentialBadge } from '../shared/PotentialBadge';
import { PlayerConversationModal } from '../shared/PlayerConversationModal';
import { FitnessBadge } from '../shared/FitnessBadge';
import { MoraleBadge } from '../shared/MoraleBadge';
import { RatingBadge } from '../shared/RatingBadge';
import { formatValue } from '../../utils/utilities';
import { useTableFilters } from '../../hooks/useTableFilters';
import { TableHeader } from '../shared/TableHeader';
import { AdvancedFilterModal } from '../shared/AdvancedFilterModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAmbulance, faFaceMeh, faLayerGroup, faGamepad } from '@fortawesome/free-solid-svg-icons';
import { TeamHelpModal } from '../shared/TeamHelpModal';
import { OppositionTeamDebugPanel } from './OppositionTeamDebugPanel';
import { setTeamAccentColor, resetTeamAccentColor } from '../../utils/accentColorUtils';
import './dashboard/TeamStatsPanel.css';
import './TeamScreen.css';

interface TeamScreenProps {
    team: TeamInLeague;
    leagueAverageSkill: number;
    season: number;
    gameState: GameState;
    pushView: (view: View) => void;
    activeTab?: 'list' | 'depth' | 'fixtures' | 'morale' | 'injuries';
    updateCurrentView: (propsToUpdate: Partial<View>) => void;
    adsEnabled: boolean;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    showHiddenInfo?: boolean;
    isPlayerTeam?: boolean;
    // FIX: Add missing 'dismiss_concerns' and 'assert_authority' to onPlayerConversation choice types.
    onPlayerConversation: (playerId: number, choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority') => Promise<{ success: boolean; message: string; }>;
    onOfferFinancialIncentive: (playerId: number, type: 'wage' | 'bonus', amount: number) => Promise<{ success: boolean; message: string; }>;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
}

const positionOrder: Record<Position, number> = { GK: 1, DEF: 2, MID: 3, FWD: 4 };

const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
};

const InjuriesView: React.FC<{
    team: TeamInLeague;
    pushView: (view: View) => void;
}> = ({ team, pushView }) => {
    const injuredPlayers = useMemo(() => {
        return team.squad.filter(p => p.injury).sort((a, b) => {
            // Sort by injury duration (shorter recoveries first)
            return (a.injury?.duration || 0) - (b.injury?.duration || 0);
        });
    }, [team.squad]);

    return (
        <div className="panel-content">
            {injuredPlayers.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px' }}>No injured players. The squad is fully fit!</p>
            ) : (
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Player</th>
                                <th>Position</th>
                                <th>Injury Type</th>
                                <th>Weeks Remaining</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {injuredPlayers.map(player => {
                                const weeksRemaining = player.injury?.duration || 0;
                                const injuryType = player.injury?.type || 'Unknown';
                                const isLongTerm = weeksRemaining > 4;

                                return (
                                    <tr key={player.id}>
                                        <td>
                                            <button className="link-style" onClick={() => pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id })}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <PlayerFace player={player} teamColor={team.colors.primary} />
                                                    <span>{player.name}</span>
                                                </div>
                                            </button>
                                        </td>
                                        <td>{player.position}</td>
                                        <td style={{ color: injuryType === 'Severe' ? 'var(--negative-color)' : 'var(--text-secondary-color)' }}>
                                            {injuryType}
                                        </td>
                                        <td>
                                            <span style={{
                                                color: isLongTerm ? 'var(--negative-color)' : 'var(--positive-color)',
                                                fontWeight: isLongTerm ? 'bold' : 'normal'
                                            }}>
                                                {weeksRemaining}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '0.9em', color: 'var(--text-secondary-color)' }}>
                                                {weeksRemaining === 1 ? 'Returning soon' : weeksRemaining > 4 ? 'Long-term' : 'Short-term'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const MoraleView: React.FC<{
    team: TeamInLeague;
    onTalkToPlayer: (player: Player) => void;
    pushView: (view: View) => void;
}> = ({ team, onTalkToPlayer, pushView }) => {
    const moraleOrder = { 'Furious': 0, 'Unhappy': 1, 'Content': 2, 'Happy': 3, 'Ecstatic': 4 };

    const allPlayers = useMemo(() => {
        return team.squad.sort((a, b) => {
            // Sort by morale (worst first)
            const moraleAValue = moraleOrder[a.morale as keyof typeof moraleOrder] ?? 5;
            const moraleBValue = moraleOrder[b.morale as keyof typeof moraleOrder] ?? 5;
            if (moraleAValue !== moraleBValue) return moraleAValue - moraleBValue;

            // Then by wantsToTalk (those wanting to talk first)
            if (a.wantsToTalk && !b.wantsToTalk) return -1;
            if (!a.wantsToTalk && b.wantsToTalk) return 1;

            // Then by skill (highest first)
            return b.skill - a.skill;
        });
    }, [team.squad]);

    return (
        <div className="panel-content">
            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Player</th>
                            <th>Morale</th>
                            <th>Reason</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {allPlayers.map(player => {
                            const reason = player.moraleReason || (player.wantsToTalk ? 'Wants a meeting' : '—');
                            return (
                                <tr key={player.id}>
                                    <td>
                                        <button className="link-style" onClick={() => pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id })}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <PlayerFace player={player} teamColor={team.colors.primary} />
                                                <span>{player.name}</span>
                                            </div>
                                        </button>
                                    </td>
                                    <td><MoraleBadge morale={player.morale} /></td>
                                    <td>{reason}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {player.wantsToTalk && (
                                            <button className="button small" onClick={() => onTalkToPlayer(player)}>Talk</button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const TeamScreen = ({ team, leagueAverageSkill, gameState, pushView, activeTab: activeTabProp, updateCurrentView, adsEnabled, isAiPunditEnabled, showRatingsOnMatchReport, showHiddenInfo, isPlayerTeam, onPlayerConversation, onOfferFinancialIncentive, onCacheAiMatchReport }: TeamScreenProps) => {
    const [activeTab, setActiveTab] = useState<'list' | 'depth' | 'fixtures' | 'morale' | 'injuries'>(activeTabProp || 'list');
    const [comparingPlayer, setComparingPlayer] = useState<Player | null>(null);
    const [playerToTalkTo, setPlayerToTalkTo] = useState<Player | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Filter state
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters('team-squad', {
        search: '',
        sortBy: 'position_asc',
        positionFilter: 'ALL',
        minSkill: 0,
        maxSkill: 120,  // Skill cap is 120
        minAge: 16,
        maxAge: 50,     // Increased from 40 to include older players
        minValue: 0,
        maxValue: 50000000,
        favFootFilter: 'ALL',
    });

    useEffect(() => {
        setActiveTab(activeTabProp || 'list');
    }, [activeTabProp]);

    // Set team accent color when viewing a non-player team
    useEffect(() => {
        setTeamAccentColor(team.colors.primary);
        return () => {
            resetTeamAccentColor();
        };
    }, [team.colors.primary]);

    const handleTabClick = (tab: 'list' | 'depth' | 'fixtures' | 'morale' | 'injuries') => {
        soundService.playClick();
        setActiveTab(tab);
        updateCurrentView({ activeTab: tab });
    };

    const handleRowClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const getPlayerStatus = (playerId: number): string => {
        const lineup = team.tactics.lineup;
        const position = (Object.keys(lineup) as TacticalPosition[]).find(pos => lineup[pos] === playerId);
        return position || 'Reserve';
    };

    const leagueName = useMemo(() => {
        for (const ln in gameState.leagues) {
            if (gameState.leagues[ln].teams.some(t => t.name === team.name)) {
                return ln;
            }
        }
        return gameState.playerLeagueName; // fallback
    }, [gameState.leagues, team.name, gameState.playerLeagueName]);

    const filteredAndSortedSquad = useMemo(() => {
        return [...team.squad]
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .filter(p => tableFilters.positionFilter === 'ALL' || p.position === tableFilters.positionFilter)
            .filter(p => p.skill >= tableFilters.minSkill && p.skill <= tableFilters.maxSkill)
            .filter(p => p.age >= tableFilters.minAge && p.age <= tableFilters.maxAge)
            .filter(p => {
                const pValue = getPlayerValue(p, leagueName, gameState.gameType, gameState.config);
                return pValue >= tableFilters.minValue && pValue <= tableFilters.maxValue;
            })
            .filter(p => tableFilters.favFootFilter === 'ALL' || p.favoredFoot === tableFilters.favFootFilter)
            .sort((a, b) => {
                switch (tableFilters.sortBy) {
                    case 'skill_asc': return a.skill - b.skill;
                    case 'potential_desc': return b.potential - a.potential;
                    case 'potential_asc': return a.potential - b.potential;
                    case 'fitness_desc': return b.fitness - a.fitness;
                    case 'fitness_asc': return a.fitness - b.fitness;
                    case 'position_asc': return positionOrder[a.position] - positionOrder[b.position] || b.skill - a.skill;
                    case 'name_asc': return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
                    case 'name_desc': return b.name < a.name ? -1 : b.name > a.name ? 1 : 0;
                    case 'age_asc': return a.age - b.age;
                    case 'age_desc': return b.age - a.age;
                    case 'value_desc': return getPlayerValue(b, leagueName, gameState.gameType, gameState.config) - getPlayerValue(a, leagueName, gameState.gameType, gameState.config);
                    case 'value_asc': return getPlayerValue(a, leagueName, gameState.gameType, gameState.config) - getPlayerValue(b, leagueName, gameState.gameType, gameState.config);
                    case 'contract_asc': {
                        // Sort by season first, then by week (numeric comparison, not lexicographic)
                        const aSeason = a.contract.expirySeason;
                        const bSeason = b.contract.expirySeason;
                        if (aSeason !== bSeason) return aSeason - bSeason;
                        // Both have same season, compare weeks (both use WEEKS_PER_SEASON so always equal)
                        return 0;
                    }
                    case 'contract_desc': {
                        // Sort by season descending, then by week descending
                        const aSeason = a.contract.expirySeason;
                        const bSeason = b.contract.expirySeason;
                        if (aSeason !== bSeason) return bSeason - aSeason;
                        return 0;
                    }
                    case 'skill_desc':
                    default:
                        return b.skill - a.skill;
                }
            });
    }, [team.squad, tableFilters, leagueName, gameState.gameType, gameState.config]);

    return (
        <div className="team-screen-section">
            <div className="team-screen-header-container">
                <div className="team-screen-header-content">
                    <div className="team-screen-header-row-1">
                        <div className="team-screen-header-name-section">
                            <h1>{team.badge && <TeamBadge badge={team.badge} color={team.colors.primary} size="md" />}{team.name}</h1>
                            <div className="team-screen-header-meta">
                                {(() => {
                                    const league = gameState.leagues[leagueName];
                                    const position = league ? league.teams.findIndex(t => t.name === team.name) + 1 : '--';
                                    const ordinalSuffix = position !== '--' ? getOrdinalSuffix(position) : '';
                                    return (
                                        <>
                                            <button
                                                className="link-style"
                                                onClick={() => { soundService.playClick(); pushView({ type: 'competitions', title: 'Competitions', leagueName: leagueName, activeTab: 'league' }); }}
                                                title="View league standings"
                                            >
                                                {leagueName}
                                            </button>
                                            {position !== '--' && (
                                                <span>(<strong>{position}{ordinalSuffix}</strong>)</span>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        <button
                            className="button"
                            onClick={() => setIsHelpOpen(true)}
                            style={{ padding: '8px 12px', fontSize: '0.9em', whiteSpace: 'nowrap' }}
                            title="Help"
                        >
                            Help
                        </button>
                    </div>
                </div>
            </div>

            <div className="panel team-stats-panel">
                <div className="panel-header">Team Squad</div>
                <div className="tabs">
                    <button className={`tab-button ${activeTab === 'list' ? 'active' : ''}`} onClick={() => handleTabClick('list')} title="Squad List">
                        <TeamBadge badge={team.badge} color={activeTab === 'list' ? 'var(--secondary-accent-color)' : team.colors.primary} size="sm" />
                    </button>
                    <button className={`tab-button ${activeTab === 'depth' ? 'active' : ''}`} onClick={() => handleTabClick('depth')} title="Depth Chart"><FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '1.2em' }} /></button>
                    <button className={`tab-button ${activeTab === 'morale' ? 'active' : ''}`} onClick={() => handleTabClick('morale')} title="Morale"><FontAwesomeIcon icon={faFaceMeh} style={{ fontSize: '1.2em' }} /></button>
                    <button className={`tab-button ${activeTab === 'injuries' ? 'active' : ''}`} onClick={() => handleTabClick('injuries')} title="Injuries"><FontAwesomeIcon icon={faAmbulance} style={{ fontSize: '1.2em' }} /></button>
                    <button className={`tab-button ${activeTab === 'fixtures' ? 'active' : ''}`} onClick={() => handleTabClick('fixtures')} title="Fixtures"><FontAwesomeIcon icon={faGamepad} style={{ fontSize: '1.2em' }} /></button>
                </div>
                {adsEnabled && <AdPlaceholder />}
            {activeTab === 'list' && (
                <>
                    <TableHeader
                        searchValue={tableFilters.search}
                        onSearchChange={(value) => updateFilter('search', value)}
                        searchPlaceholder="Search player names..."
                        sortValue={tableFilters.sortBy}
                        onSortChange={(value) => updateFilter('sortBy', value)}
                        sortOptions={[
                            { label: 'Position (default)', value: 'position_asc' },
                            { label: 'Skill (High to Low)', value: 'skill_desc' },
                            { label: 'Skill (Low to High)', value: 'skill_asc' },
                            { label: 'Potential (High to Low)', value: 'potential_desc' },
                            { label: 'Potential (Low to High)', value: 'potential_asc' },
                            { label: 'Fitness (High to Low)', value: 'fitness_desc' },
                            { label: 'Fitness (Low to High)', value: 'fitness_asc' },
                            { label: 'Age (Young to Old)', value: 'age_asc' },
                            { label: 'Age (Old to Young)', value: 'age_desc' },
                            { label: 'Value (High to Low)', value: 'value_desc' },
                            { label: 'Value (Low to High)', value: 'value_asc' },
                            { label: 'Contract (Soonest)', value: 'contract_asc' },
                            { label: 'Contract (Latest)', value: 'contract_desc' },
                            { label: 'Name (A to Z)', value: 'name_asc' },
                            { label: 'Name (Z to A)', value: 'name_desc' },
                        ]}
                        onFilterClick={() => setIsFilterModalOpen(true)}
                        onClearFilters={clearFilters}
                        hasActiveFilters={hasActiveFilters}
                    />
                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Pos</th>
                                    <th>Cat</th>
                                    <th>Foot</th>
                                    <th>Age</th>
                                    <th>Status</th>
                                    <th>SKL</th>
                                    <th>Rating</th>
                                    <th>Pot</th>
                                    <th>Fit</th>
                                    <th>Morale</th>
                                    <th>Form</th>
                                    <th>Contract</th>
                                    <th>Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedSquad.map(player => (
                                    <tr key={player.id} className="clickable-row" onClick={() => handleRowClick(player)}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <PlayerFace player={player} teamColor={team.colors.primary} />
                                                {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} />}
                                                <span>{player.name}</span>
                                                {player.isListed && <span style={{ marginLeft: '8px', fontSize: '0.75em', fontWeight: 'normal', color: 'var(--warning-color)', border: '1px solid var(--warning-color)', padding: '2px 6px', borderRadius: '4px' }}>Listed</span>}
                                                {player.wantsToTalk && (
                                                    <span
                                                        title="Player wants to talk"
                                                        onClick={(e) => { e.stopPropagation(); soundService.playClick(); setPlayerToTalkTo(player); }}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        💬
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{player.position}</td>
                                        <td title={player.favoredPositionCategory}>{player.favoredPositionCategory?.substring(0, 3)}</td>
                                        <td title={player.favoredFoot}>{player.favoredFoot?.substring(0, 3)}</td>
                                        <td>{player.age}</td>
                                        <td>{getPlayerStatus(player.id)}</td>
                                        <td>{player.skill}</td>
                                        <td>
                                            <RatingBadge 
                                                rating={calculateStarRating(player.skill + player.form, leagueAverageSkill)}
                                                leagueName={leagueName}
                                            />
                                        </td>
                                        <td><PotentialBadge potential={player.potential} /></td>
                                        <td><FitnessBadge fitness={player.fitness} /></td>
                                        <td><MoraleBadge morale={player.morale} /></td>
                                        <td>
                                            {player.form > 0 && <span className="positive">+{player.form}</span>}
                                            {player.form < 0 && <span className="negative">{player.form}</span>}
                                            {player.form === 0 && <span className="text-secondary">-</span>}
                                        </td>
                                        <td style={{ fontWeight: player.contract.expirySeason <= gameState.season + 1 ? 'bold' : 'normal', color: player.contract.expirySeason <= gameState.season ? 'var(--negative-color)' : 'inherit' }}>
                                            S{player.contract.expirySeason}W{gameState.config.WEEKS_PER_SEASON}
                                        </td>
                                        <td>{formatValue(player.marketValue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            {activeTab === 'depth' && (
                <SquadDepthChart squad={team.squad} tactics={team.tactics} pushView={pushView} teamColor={team.colors.primary} />
            )}
            {activeTab === 'morale' && (
                <MoraleView
                    team={team}
                    onTalkToPlayer={(player) => setPlayerToTalkTo(player)}
                    pushView={pushView}
                />
            )}
            {activeTab === 'injuries' && (
                <InjuriesView
                    team={team}
                    pushView={pushView}
                />
            )}
            {activeTab === 'fixtures' && (
                <TeamFixtures
                    teamName={team.name}
                    gameState={gameState}
                    isPlayerTeam={team.name === gameState.playerTeamName}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    showRatingsOnMatchReport={showRatingsOnMatchReport}
                    onCacheAiMatchReport={onCacheAiMatchReport}
                />
            )}

            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                filterOptions={[
                    { label: 'Position', type: 'select', key: 'positionFilter', options: [
                        { label: 'All Positions', value: 'ALL' },
                        { label: 'Goalkeeper', value: 'GK' },
                        { label: 'Defender', value: 'DEF' },
                        { label: 'Midfielder', value: 'MID' },
                        { label: 'Forward', value: 'FWD' },
                    ]},
                    { label: 'Preferred Foot', type: 'select', key: 'favFootFilter', options: [
                        { label: 'All Feet', value: 'ALL' },
                        { label: 'Left', value: 'Left' },
                        { label: 'Right', value: 'Right' },
                    ]},
                    { label: 'Min Skill', type: 'range', key: 'minSkill', min: 0, max: 120, step: 1 },
                    { label: 'Max Skill', type: 'range', key: 'maxSkill', min: 0, max: 120, step: 1 },
                    { label: 'Min Age', type: 'range', key: 'minAge', min: 16, max: 50, step: 1 },
                    { label: 'Max Age', type: 'range', key: 'maxAge', min: 16, max: 50, step: 1 },
                    { label: 'Min Value', type: 'range', key: 'minValue', min: 0, max: 50000000, step: 100000 },
                    { label: 'Max Value', type: 'range', key: 'maxValue', min: 0, max: 50000000, step: 100000 },
                ]}
                title="Team Squad Filters"
            />

            {comparingPlayer && gameState && (
                <PlayerComparisonModal
                    isOpen={true}
                    onClose={() => setComparingPlayer(null)}
                    playerA_initial={comparingPlayer}
                    playerTeam={team}
                    gameState={gameState}
                    isAiPunditEnabled={isAiPunditEnabled}
                />
            )}
            {playerToTalkTo && (
                <PlayerConversationModal
                    player={playerToTalkTo}
                    onClose={() => setPlayerToTalkTo(null)}
                    onAction={onPlayerConversation}
                    onOfferFinancialIncentive={onOfferFinancialIncentive}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    canTalk={gameState.currentWeek > (gameState.managerTalkCooldownEnd?.week || 0) || gameState.season > (gameState.managerTalkCooldownEnd?.season || 0)}
                    cooldown={gameState.managerTalkCooldownEnd}
                />
            )}
            </div>

            {showHiddenInfo && !isPlayerTeam && (
                <OppositionTeamDebugPanel team={team} />
            )}

            <TeamHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </div>
    );
};